import { useEffect, useState } from "react";
import { Check, X } from "lucide-react";
import { useI18n, LANGUAGES } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const KEY = "fender_lang_picked";

/**
 * One-time first-login language picker. Shows a modal on the very first
 * authenticated session on this device. Saves the selection to:
 *   - localStorage (so it never appears again on this device)
 *   - the language i18n context (so translation kicks in immediately)
 *   - the server prefs (so the choice follows the garage across devices,
 *     if the user has role owner or branch_manager).
 * Users who explicitly pick "English" also dismiss the splash forever.
 */
export function LanguagePickerSplash() {
  const { user } = useAuth();
  const { lang, setLang } = useI18n();
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(lang || "en");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    const seen = localStorage.getItem(KEY);
    if (seen === "1") return;
    // Show splash after tiny delay so app finishes mounting first
    const t = setTimeout(() => setOpen(true), 400);
    return () => clearTimeout(t);
  }, [user]);

  const dismiss = () => {
    localStorage.setItem(KEY, "1");
    setOpen(false);
  };

  const confirm = async () => {
    setSaving(true);
    try {
      if (picked !== lang) setLang(picked);
      // Best-effort server-side save. Silent on failure so we never block.
      if (["owner", "branch_manager"].includes(user?.role)) {
        try {
          const cur = await api.get("/settings/preferences");
          await api.patch("/settings/preferences", {
            ...(cur.data || {}), language: picked,
          });
        } catch { /* non-fatal */ }
      }
    } finally {
      dismiss();
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
         data-testid="lang-splash-backdrop">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden"
           data-testid="lang-splash">
        <header className="px-6 py-5 bg-gradient-to-br from-[#1d3557] to-[#457b9d] text-white flex items-start justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest font-semibold text-[#a8dadc] mb-1">
              Welcome to Fender
            </div>
            <h2 className="text-[20px] font-bold leading-tight">
              Choose your preferred language
            </h2>
            <p className="text-[12.5px] text-[#a8dadc] mt-1">
              The whole app will show up in this language. You can change it any time in Settings.
            </p>
          </div>
          <button onClick={dismiss} data-testid="lang-splash-skip"
                  className="w-8 h-8 rounded-md text-white/70 hover:text-white hover:bg-white/10 grid place-items-center">
            <X size={18} />
          </button>
        </header>
        <div className="p-4 grid grid-cols-2 gap-2 max-h-[380px] overflow-y-auto"
             data-testid="lang-splash-options">
          {LANGUAGES.map((l) => {
            const active = picked === l.code;
            return (
              <button key={l.code} onClick={() => setPicked(l.code)}
                      data-testid={`lang-splash-${l.code}`}
                      className={`h-16 px-3 rounded-lg border text-left transition-all
                                  ${active
                                    ? "border-[#e63946] bg-[#e63946]/8 ring-2 ring-[#e63946]/30"
                                    : "border-[#E4E7EC] hover:border-[#457b9d] hover:bg-[#F1FAEE]"}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[15px] font-bold text-[#1d3557] leading-tight">
                      {l.label}
                    </div>
                    <div className="text-[11px] text-[#667085] mt-0.5">{l.native}</div>
                  </div>
                  {active && (
                    <div className="w-6 h-6 rounded-full bg-[#e63946] text-white grid place-items-center">
                      <Check size={13} />
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
        <footer className="px-6 py-3.5 border-t border-[#E4E7EC] bg-[#F7F9FC] flex items-center justify-between gap-3">
          <button onClick={dismiss} data-testid="lang-splash-later"
                  className="h-10 px-3 rounded-md text-[12.5px] font-semibold text-[#667085] hover:text-[#1d3557]">
            Maybe later
          </button>
          <button onClick={confirm} disabled={saving}
                  data-testid="lang-splash-confirm"
                  className="h-10 px-5 rounded-md bg-[#e63946] hover:bg-[#d02f3c] text-white text-[12.5px] font-semibold disabled:opacity-50">
            {saving ? "Saving…" : "Use this language"}
          </button>
        </footer>
      </div>
    </div>
  );
}
